import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { TrendingUp } from "lucide-react";
import { StoryCoverTile } from "@/components/ui/story-cover-tile";
import { Carousel } from "@/components/ui/carousel";
import { formatWordCount, formatRelativeTime } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────

interface TrendingStory {
  id: string;
  title: string;
  slug: string;
  coverUrl: string | null;
  chapterCount: number;
  totalWordCount: number;
  authorName: string;
  updatedAt: Date | null;
}

// ─── Component ───────────────────────────────────────────

interface TrendingCarouselProps {
  stories: TrendingStory[];
  title?: string;
  showNewBadge?: boolean;
}

/**
 * A horizontal scrollable carousel of trending stories.
 * Only renders if there are stories to show.
 */
export function TrendingCarousel(t0) {
  const $ = _c(9);
  const {
    stories,
    title: t1,
    showNewBadge: t2
  } = t0;
  const title = t1 === undefined ? "New & Trending" : t1;
  const showNewBadge = t2 === undefined ? true : t2;
  if (stories.length === 0) {
    return null;
  }
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TrendingUp className="size-6 text-accent" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== showNewBadge || $[2] !== stories) {
    let t5;
    if ($[4] !== showNewBadge) {
      t5 = (story, index) => <Link key={story.id} href={`/story/${story.slug}`} className="group flex-shrink-0 w-64 md:w-72 snap-start flex flex-col"><StoryCoverTile coverUrl={story.coverUrl} title={story.title} sizes="(min-width: 768px) 288px, 256px" priority={index === 0} badge={showNewBadge ? {
          label: "New",
          position: "left",
          tone: "accent"
        } : undefined} shadow={false} className="mb-3"><div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent items-end p-4 hidden dark:flex"><div className="flex items-center gap-2 text-white text-xs"><span className="opacity-80">{story.chapterCount} {story.chapterCount === 1 ? "ch" : "chs"}</span><span className="opacity-50">•</span><span className="opacity-80">{formatWordCount(story.totalWordCount)} words</span></div></div><div className="absolute bottom-0 inset-x-0 h-3/5 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-4 dark:hidden"><h4 className="font-bold text-white line-clamp-2 drop-shadow-md mb-1 leading-snug">{story.title}</h4><p className="text-white/80 text-[11px] drop-shadow-md mb-1.5 line-clamp-1">by {story.authorName} • Updated {formatRelativeTime(story.updatedAt)}</p><div className="flex items-center gap-2 text-white/90 text-xs drop-shadow-md"><span>{story.chapterCount} {story.chapterCount === 1 ? "ch" : "chs"}</span><span className="opacity-50">•</span><span>{formatWordCount(story.totalWordCount)} words</span></div></div></StoryCoverTile><div className="hidden dark:block"><h4 className="font-bold text-slate-900 dark:text-white truncate">{story.title}</h4><p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">by {story.authorName} • Updated {formatRelativeTime(story.updatedAt)}</p></div></Link>;
      $[4] = showNewBadge;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t4 = stories.map(t5);
    $[1] = showNewBadge;
    $[2] = stories;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[6] !== t4 || $[7] !== title) {
    t5 = <Carousel title={title} icon={t3}>{t4}</Carousel>;
    $[6] = t4;
    $[7] = title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}